import type { SeoLandingContent } from "@/components/SeoLandingTemplate";

/**
 * Content for high-intent SEO landing pages. These pages exist to capture
 * specific buyer search intent (urgent, hard-to-find, supplier queries, etc.)
 * and are intentionally NOT linked from the header, footer, or homepage.
 *
 * Each entry maps 1:1 to a route in App.tsx and a sitemap entry.
 * Add a new entry here, register the route, and rebuild the sitemap to ship a
 * new keyword-targeted landing page.
 */
export const SEO_LANDINGS: Record<string, SeoLandingContent> = {
  "hard-to-find-electrical-components": {
    slug: "hard-to-find-electrical-components",
    breadcrumbLabel: "Hard-to-Find Electrical Components",
    eyebrow: "Hard-to-find sourcing",
    h1: "Hard-to-Find Electrical Components, Sourced Fast",
    heroSub:
      "Discontinued SKUs, allocated parts, obsolete modules — when local distributors say 'no stock,' we locate inventory across a verified U.S. supplier network and deliver to LATAM.",
    metaTitle: "Hard-to-Find Electrical Components | Fast Sourcing",
    metaDescription:
      "Source hard-to-find electrical components fast. Discontinued, allocated, and obsolete SKUs located through a verified U.S. supplier network. Quotes in hours.",
    pain: {
      title: "Why buyers search for hard-to-find components",
      items: [
        "Local distributor shows 'no stock' or quotes 12+ week lead times",
        "OEM has discontinued the SKU but plant still runs on it",
        "Allocated inventory is tied up across the U.S. and Europe",
        "Aftermarket and surplus channels are fragmented and hard to verify",
        "Project deadline can't absorb a 3-month wait",
      ],
    },
    provide: {
      title: "What we provide",
      items: [
        {
          title: "Cross-supplier search",
          body: "We query multiple verified U.S. suppliers per request — not a single distributor inventory.",
        },
        {
          title: "Real availability",
          body: "We confirm stock and lead time before quoting — no placeholder dates.",
        },
        {
          title: "Firm landed pricing",
          body: "Product, freight, duties, and final-mile delivery calculated upfront (DDP).",
        },
        {
          title: "Delivery coordination",
          body: "We handle export, customs, and final-mile delivery across LATAM end-to-end.",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh",
      items: [
        {
          title: "Built for the hard ones",
          body: "We don't compete on commodity stock. We focus on parts that local channels can't access.",
        },
        {
          title: "Hours, not days",
          body: "Most quotes come back within hours, including availability and a firm landed price.",
        },
        {
          title: "Verified U.S. network",
          body: "Suppliers are vetted for stock accuracy, fulfillment reliability, and compliance.",
        },
        {
          title: "Single point of contact",
          body: "One sourcing manager owns your request from quote through delivery.",
        },
      ],
    },
    expectations: {
      title: "What to expect",
      items: [
        {
          title: "Submit a SKU, photo, or spec",
          body: "A part number gets the fastest, most accurate quote. Photos and spec sheets also work.",
        },
        {
          title: "Availability confirmed in hours",
          body: "We confirm stock with verified suppliers before quoting — no speculative pricing.",
        },
        {
          title: "Firm landed price",
          body: "Quote includes product, freight, duties, taxes, and final-mile delivery (DDP).",
        },
        {
          title: "Delivery in days, not months",
          body: "Typical delivery to major LATAM destinations runs in days for in-stock items.",
        },
      ],
    },
    finalCta: {
      title: "Send the part number — get availability in hours",
      body: "Submit your hard-to-find component and we'll come back with confirmed stock and landed pricing.",
    },
  },

  "urgent-electrical-components": {
    slug: "urgent-electrical-components",
    breadcrumbLabel: "Urgent Electrical Components",
    eyebrow: "Urgent sourcing",
    h1: "Urgent Electrical Components — Quoted in Hours",
    heroSub:
      "Production line down, project at risk, or replacement part needed this week? We respond in hours with confirmed availability and firm landed pricing across LATAM.",
    metaTitle: "Urgent Electrical Components Supplier | Quotes in Hours",
    metaDescription:
      "Urgent electrical components supplier for industrial buyers. Confirmed availability and firm landed pricing in hours. Delivered across LATAM in days.",
    pain: {
      title: "When every hour costs money",
      items: [
        "Production line down — every hour of downtime burns revenue",
        "Critical replacement breaker or contactor is out of local stock",
        "Project commissioning blocked by a missing PLC module or drive",
        "Local quote turnaround is 3–5 days and you don't have 3–5 days",
        "Need a confirmed delivery date, not a 'maybe in 8 weeks'",
      ],
    },
    provide: {
      title: "What we provide",
      items: [
        {
          title: "Same-day response",
          body: "Urgent requests are flagged and routed to a sourcing manager immediately.",
        },
        {
          title: "Confirmed availability",
          body: "We don't quote what we can't ship. Stock is verified before pricing goes out.",
        },
        {
          title: "Expedited freight",
          body: "Air freight options included where transit time is the constraint.",
        },
        {
          title: "DDP delivery",
          body: "We handle export docs, duties, and final-mile so the part lands on your dock.",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh for urgent sourcing",
      items: [
        {
          title: "Operational, not transactional",
          body: "We're built around urgent procurement — not a generic distributor catalog.",
        },
        {
          title: "Multiple suppliers, one request",
          body: "We query the network in parallel so you get the fastest available stock, not the first hit.",
        },
        {
          title: "WhatsApp + email",
          body: "Send a SKU, a photo, or a spec via WhatsApp. We respond in the same channel.",
        },
        {
          title: "LATAM execution",
          body: "We deliver to Mexico, Colombia, Peru, Chile, Brazil, and Argentina with predictable transit.",
        },
      ],
    },
    expectations: {
      title: "Urgent request flow",
      items: [
        {
          title: "Send the request",
          body: "Part number + destination is enough to start. WhatsApp is fastest for urgent jobs.",
        },
        {
          title: "Availability in hours",
          body: "We come back with verified stock, lead time, and freight options.",
        },
        {
          title: "Firm landed price",
          body: "Quote includes product, expedited freight, duties, and final-mile delivery.",
        },
        {
          title: "Pickup and ship",
          body: "Once approved, we coordinate pickup, export, and customs to your destination.",
        },
      ],
    },
    finalCta: {
      title: "Send your urgent request now",
      body: "Confirmed availability and firm landed pricing within hours — not days.",
    },
  },

  "electrical-components-supplier": {
    slug: "electrical-components-supplier",
    breadcrumbLabel: "Electrical Components Supplier",
    eyebrow: "Industrial supplier",
    h1: "Electrical Components Supplier for Industrial Buyers",
    heroSub:
      "A single sourcing partner across breakers, contactors, PLCs, drives, and switchgear — backed by a verified U.S. supplier network and DDP delivery to LATAM.",
    metaTitle: "Electrical Components Supplier — Industrial Sourcing Partner",
    metaDescription:
      "Electrical components supplier for EPCs, contractors, and plant operators. Verified U.S. network, firm landed pricing, DDP delivery across LATAM.",
    pain: {
      title: "Why buyers consolidate sourcing with us",
      items: [
        "Managing 10+ distributor relationships per project",
        "Inconsistent quote turnaround and pricing transparency",
        "Local stock shortages on common SKUs across multiple brands",
        "No single owner for cross-border freight, duties, and final-mile",
        "Need one supplier accountable from quote to delivered part",
      ],
    },
    provide: {
      title: "What we supply",
      items: [
        {
          title: "Circuit protection",
          body: "MCCBs, ACBs, contactors, overload relays from major brands.",
        },
        {
          title: "Automation & control",
          body: "PLC modules, I/O cards, HMIs, control components, sensors.",
        },
        {
          title: "Power & distribution",
          body: "Transformers, switchgear components, power supplies, drives.",
        },
        {
          title: "Pre-configured packages",
          body: "Built-to-spec power kits when you need a system, not just a SKU.",
        },
      ],
    },
    categories: {
      title: "Browse by component family",
      items: [
        { name: "MCCB breakers", href: "/fast-access-power-components/mccb-breakers" },
        { name: "PLC modules", href: "/fast-access-power-components/plc-modules" },
        { name: "Contactors & overloads", href: "/fast-access-power-components/contactors-overloads" },
        { name: "VFD drives", href: "/fast-access-power-components/vfd-drives" },
        { name: "Industrial power supplies", href: "/fast-access-power-components/industrial-power-supplies" },
        { name: "Industrial sensors", href: "/fast-access-power-components/industrial-sensors" },
      ],
    },
    whyUs: {
      title: "Why work with us as your supplier",
      items: [
        {
          title: "One contact, full ownership",
          body: "A single sourcing manager owns your account end-to-end.",
        },
        {
          title: "Verified U.S. supplier base",
          body: "Pre-vetted distributors and aftermarket sources — not random surplus.",
        },
        {
          title: "Firm landed pricing (DDP)",
          body: "Product, freight, duties, and delivery in one quote. No border surprises.",
        },
        {
          title: "EN · ES · PT support",
          body: "We work in your language across the LATAM and U.S. timezones.",
        },
      ],
    },
    expectations: {
      title: "Working with us",
      items: [
        {
          title: "Open an account or send an RFQ",
          body: "No onboarding fee. Send a SKU list or project BOM to start.",
        },
        {
          title: "Receive quotes in hours",
          body: "Most line items come back same-day with confirmed availability.",
        },
        {
          title: "Approve and ship",
          body: "We handle freight, customs, and final-mile to your destination.",
        },
        {
          title: "Reorder with one message",
          body: "Repeat orders move faster — your supplier history stays with us.",
        },
      ],
    },
    finalCta: {
      title: "Make us your electrical components supplier",
      body: "Send an RFQ — we'll come back with availability, firm landed pricing, and a delivery date.",
    },
  },

  "industrial-electrical-components": {
    slug: "industrial-electrical-components",
    breadcrumbLabel: "Industrial Electrical Components",
    eyebrow: "Industrial sourcing",
    h1: "Industrial Electrical Components for Plants & EPCs",
    heroSub:
      "Standardized industrial electrical components — circuit protection, automation, drives, and distribution — sourced from verified U.S. suppliers and delivered to LATAM with full landed cost.",
    metaTitle: "Industrial Electrical Components | Sourcing & Supply",
    metaDescription:
      "Industrial electrical components for plants, EPCs, and contractors. MCCBs, PLCs, drives, and switchgear sourced fast with DDP delivery across LATAM.",
    pain: {
      title: "What industrial buyers need from a sourcing partner",
      items: [
        "Reliable supply on widely used SKUs across major brands",
        "Firm landed cost — not 'EXW + you figure out the rest'",
        "Sourcing managers who understand industrial spec language",
        "Single accountable supplier across the U.S. → LATAM corridor",
        "Predictable transit times for project planning",
      ],
    },
    provide: {
      title: "What we cover",
      items: [
        {
          title: "Circuit protection",
          body: "MCCBs, ACBs, contactors, overload relays — major brands, multiple frame sizes.",
        },
        {
          title: "Automation & control",
          body: "PLC modules, I/O cards, HMIs, control relays, industrial sensors.",
        },
        {
          title: "Drives & motor control",
          body: "VFDs, soft starters, and motor protection devices for industrial loads.",
        },
        {
          title: "Power distribution",
          body: "Transformers, panel components, power supplies, switchgear parts.",
        },
      ],
    },
    whyUs: {
      title: "Built for industrial procurement",
      items: [
        {
          title: "Spec-driven sourcing",
          body: "We validate frame size, voltage, current, and trip unit before quoting.",
        },
        {
          title: "Multi-brand coverage",
          body: "ABB, Siemens, Schneider, Eaton, Allen-Bradley, and more from a single RFQ.",
        },
        {
          title: "Verified U.S. supply chain",
          body: "Distributors and aftermarket sources vetted for accuracy and compliance.",
        },
        {
          title: "DDP to LATAM",
          body: "We handle export, customs, and final-mile delivery as one landed cost.",
        },
      ],
    },
    expectations: {
      title: "How a typical sourcing run works",
      items: [
        {
          title: "Send the BOM or part list",
          body: "SKUs, quantities, and destination are enough to start. We'll flag any spec gaps.",
        },
        {
          title: "Quote returned in hours",
          body: "Each line includes confirmed availability, lead time, and landed price.",
        },
        {
          title: "Approve and ship",
          body: "We coordinate pickup, export, customs, and final-mile delivery to your site.",
        },
        {
          title: "Track to delivery",
          body: "Order status and tracking are shared throughout the shipment lifecycle.",
        },
      ],
    },
    finalCta: {
      title: "Send your BOM — get a landed quote in hours",
      body: "We source industrial electrical components across major brands and deliver DDP across LATAM.",
    },
  },

  "electrical-procurement-services": {
    slug: "electrical-procurement-services",
    breadcrumbLabel: "Electrical Procurement Services",
    eyebrow: "Procurement services",
    h1: "Electrical Procurement Services — From SKU to Delivered",
    heroSub:
      "End-to-end procurement for industrial electrical components: sourcing, supplier vetting, landed pricing, export, customs, and final-mile delivery — owned by a single team.",
    metaTitle: "Electrical Procurement Services for Industrial Buyers",
    metaDescription:
      "Electrical procurement services covering sourcing, landed pricing, export, customs, and DDP delivery to LATAM. One accountable team from RFQ to delivered.",
    pain: {
      title: "What's broken in traditional procurement",
      items: [
        "Quotes scattered across 5–10 distributors per project",
        "Freight, duties, and final-mile handled by separate vendors",
        "No accountability when a shipment misses the date",
        "Hidden costs added at the border after PO is issued",
        "Procurement teams stretched across hundreds of line items",
      ],
    },
    provide: {
      title: "What our procurement service covers",
      items: [
        {
          title: "Sourcing & supplier vetting",
          body: "We identify verified U.S. suppliers per SKU and validate stock before quoting.",
        },
        {
          title: "Landed cost calculation",
          body: "Product, freight, duties, taxes, and final-mile in one transparent quote.",
        },
        {
          title: "Export & customs",
          body: "We handle commercial invoices, HS codes, and customs clearance end-to-end.",
        },
        {
          title: "Final-mile delivery",
          body: "Coordinated delivery to plant, warehouse, or job site across LATAM.",
        },
      ],
    },
    whyUs: {
      title: "Why buyers outsource procurement to us",
      items: [
        {
          title: "One team, full lifecycle",
          body: "RFQ to delivered — same sourcing manager, same accountability.",
        },
        {
          title: "Volume leverage",
          body: "Aggregated demand across our network gets you better pricing per SKU.",
        },
        {
          title: "Compliance built in",
          body: "Export controls, HS classification, and country-specific docs handled.",
        },
        {
          title: "Faster cycle times",
          body: "Hours for quotes, days for delivery on in-stock items.",
        },
      ],
    },
    expectations: {
      title: "How engagement works",
      items: [
        {
          title: "Send a project BOM or single SKU",
          body: "We work at any scale — single replacement parts to multi-line industrial BOMs.",
        },
        {
          title: "Receive a landed quote",
          body: "Each line includes verified stock, lead time, and DDP pricing.",
        },
        {
          title: "Approve and we execute",
          body: "We handle pickup, export, customs, and final-mile delivery.",
        },
        {
          title: "Receive and reorder",
          body: "Tracking shared through delivery; reorders move faster on file.",
        },
      ],
    },
    finalCta: {
      title: "Outsource the procurement, keep the timeline",
      body: "Send your BOM and we'll come back with a landed quote — sourcing through delivery owned by us.",
    },
  },

  "global-electrical-sourcing": {
    slug: "global-electrical-sourcing",
    breadcrumbLabel: "Global Electrical Sourcing",
    eyebrow: "Global sourcing",
    h1: "Global Electrical Sourcing for LATAM Industrial Buyers",
    heroSub:
      "Access fragmented global supply through a single sourcing layer. We locate inventory across the U.S. and verified channels, then deliver to LATAM with full landed cost.",
    metaTitle: "Global Electrical Sourcing — U.S. Supply to LATAM",
    metaDescription:
      "Global electrical sourcing for LATAM industrial buyers. Verified U.S. supplier network, firm landed pricing, and DDP delivery to Mexico, Colombia, Peru, and more.",
    pain: {
      title: "Why local sourcing isn't enough",
      items: [
        "Local distributors carry a fraction of available global supply",
        "Same SKU often has 30–60% price variance across the U.S. market",
        "Allocated inventory in Europe is invisible to local channels",
        "Cross-border procurement is operationally heavy without a partner",
        "Single-supplier dependency creates schedule risk",
      ],
    },
    provide: {
      title: "What our global sourcing layer provides",
      items: [
        {
          title: "Verified U.S. network",
          body: "Distributors and aftermarket sources pre-vetted for stock accuracy.",
        },
        {
          title: "Multi-source comparison",
          body: "We query the network in parallel and surface the best stock + price combo.",
        },
        {
          title: "Cross-border execution",
          body: "Export, customs, and DDP delivery handled end-to-end.",
        },
        {
          title: "Single point of contact",
          body: "One sourcing manager owns your account regardless of supplier mix.",
        },
      ],
    },
    whyUs: {
      title: "Why use a global sourcing layer",
      items: [
        {
          title: "Access, not just inventory",
          body: "We surface availability your local channels can't see.",
        },
        {
          title: "Price arbitrage",
          body: "Same SKU, lower landed cost when supply chain math works in your favor.",
        },
        {
          title: "Risk diversification",
          body: "Multiple supplier paths per request reduce single-source schedule risk.",
        },
        {
          title: "Built for LATAM destinations",
          body: "Customs, duties, and final-mile handled across the major LATAM markets.",
        },
      ],
    },
    expectations: {
      title: "How it works",
      items: [
        {
          title: "Submit your request",
          body: "SKU list, BOM, or spec sheet — destination country and timing.",
        },
        {
          title: "Multi-source quote",
          body: "We come back with verified stock, lead time, and landed cost per line.",
        },
        {
          title: "Approve and ship",
          body: "Pickup, export, customs, and final-mile coordinated by us.",
        },
        {
          title: "Delivered DDP",
          body: "Part lands at your dock with no border surprises.",
        },
      ],
    },
    finalCta: {
      title: "Tap into global supply through one sourcing layer",
      body: "Send your request and we'll surface real availability across our verified U.S. network.",
    },
  },

  "power-distribution-equipment-supplier": {
    slug: "power-distribution-equipment-supplier",
    breadcrumbLabel: "Power Distribution Equipment Supplier",
    eyebrow: "Power distribution",
    h1: "Power Distribution Equipment Supplier — Components & Systems",
    heroSub:
      "Switchgear, transformers, panel components, ATS systems, and distribution boards — sourced from verified U.S. suppliers and delivered DDP across LATAM.",
    metaTitle: "Power Distribution Equipment Supplier | LATAM Delivery",
    metaDescription:
      "Power distribution equipment supplier for industrial sites. Switchgear, transformers, ATS, and panel components. Firm landed pricing and DDP delivery to LATAM.",
    pain: {
      title: "What plant operators and EPCs need",
      items: [
        "Distribution gear that's available now, not in 16 weeks",
        "Single supplier across switchgear, transformers, and ATS",
        "Firm landed cost for project budget commitments",
        "Predictable freight on heavy and oversized equipment",
        "DDP delivery to remote industrial sites",
      ],
    },
    provide: {
      title: "What we supply",
      items: [
        {
          title: "LV & MV switchgear components",
          body: "Breakers, busbars, panel components, and switchgear spares.",
        },
        {
          title: "Transformers",
          body: "Distribution and power transformers across major brands and ratings.",
        },
        {
          title: "ATS & transfer systems",
          body: "Automatic transfer switches and bypass equipment for critical loads.",
        },
        {
          title: "Panel & board components",
          body: "Distribution boards, sub-panels, and panel-mount components.",
        },
      ],
    },
    whyUs: {
      title: "Why source distribution equipment with us",
      items: [
        {
          title: "Single supplier across the gear",
          body: "Breakers, transformers, ATS, and panel parts on one PO.",
        },
        {
          title: "Heavy-freight expertise",
          body: "We coordinate freight on large transformers and switchgear sections.",
        },
        {
          title: "DDP across LATAM",
          body: "Delivered to plant or job site with all duties and taxes settled.",
        },
        {
          title: "Spec validation",
          body: "We confirm voltage, kVA, frame size, and config before quoting.",
        },
      ],
    },
    expectations: {
      title: "Engagement flow",
      items: [
        {
          title: "Send the spec or SLD",
          body: "Single-line diagram, transformer spec, or switchgear schedule works.",
        },
        {
          title: "Quote in hours to days",
          body: "Standard distribution gear comes back fast; engineered equipment runs days.",
        },
        {
          title: "Approve and we ship",
          body: "Heavy freight coordinated end-to-end with customs and final-mile.",
        },
        {
          title: "Delivered to site",
          body: "Coordinated unloading and delivery to industrial destinations.",
        },
      ],
    },
    finalCta: {
      title: "Source your distribution equipment in one place",
      body: "Send your spec or SLD — we'll come back with a landed quote and a delivery date.",
    },
  },

  "mccb-breaker-supplier": {
    slug: "mccb-breaker-supplier",
    breadcrumbLabel: "MCCB Breaker Supplier",
    eyebrow: "MCCB sourcing",
    h1: "MCCB Breaker Supplier — Major Brands, Fast Sourcing",
    heroSub:
      "Molded case circuit breakers from ABB, Siemens, Schneider, Eaton, and more. Single SKU or full panel — sourced from verified U.S. suppliers and delivered DDP to LATAM.",
    metaTitle: "MCCB Breaker Supplier | ABB Siemens Schneider Eaton",
    metaDescription:
      "MCCB breaker supplier covering ABB, Siemens, Schneider, and Eaton. Firm landed pricing, verified U.S. stock, and DDP delivery to LATAM in days.",
    pain: {
      title: "Why buyers source MCCBs through us",
      items: [
        "Local distributor only carries one brand or frame size",
        "Replacement breaker for legacy panel is on long lead time",
        "Project requires multi-brand MCCBs from a single PO",
        "Need confirmed stock before issuing the purchase order",
        "Cross-border freight and duties not handled by local supplier",
      ],
    },
    provide: {
      title: "What we cover",
      items: [
        {
          title: "All major brands",
          body: "ABB Tmax, Siemens 3VA, Schneider NSX, Eaton — across frame sizes.",
        },
        {
          title: "Trip units & accessories",
          body: "Electronic and thermal-magnetic trip units, shunt trips, aux contacts.",
        },
        {
          title: "Equivalents on request",
          body: "We can suggest verified equivalents when an exact SKU is constrained.",
        },
        {
          title: "Volume + replacement",
          body: "Single replacement breakers or full panel quantities — same process.",
        },
      ],
    },
    categories: {
      title: "Browse MCCB pages",
      items: [
        { name: "MCCB breakers collection", href: "/fast-access-power-components/mccb-breakers" },
        { name: "ABB Tmax T5", href: "/product/abb-tmax-t5" },
        { name: "Schneider NSX (Mexico)", href: "/blog/schneider-nsx-mexico-2026" },
        { name: "ABB breakers (LATAM)", href: "/blog/abb-breakers-availability-latam-2026" },
      ],
    },
    whyUs: {
      title: "Why source MCCBs with Switzh",
      items: [
        {
          title: "Multi-brand, single quote",
          body: "Mix ABB, Siemens, Schneider, and Eaton on one RFQ.",
        },
        {
          title: "Verified U.S. stock",
          body: "We confirm availability with the supplier before quoting.",
        },
        {
          title: "Firm landed pricing",
          body: "DDP — product, freight, duties, and final-mile in one number.",
        },
        {
          title: "LATAM delivery in days",
          body: "Standard MCCBs ship to major LATAM markets in days, not weeks.",
        },
      ],
    },
    expectations: {
      title: "How to request a quote",
      items: [
        {
          title: "Send the SKU or breaker spec",
          body: "Frame size, current, poles, trip unit type — or a part number.",
        },
        {
          title: "Availability + price in hours",
          body: "We come back with confirmed stock and DDP landed price per line.",
        },
        {
          title: "Approve and ship",
          body: "We coordinate freight, customs, and delivery to your site.",
        },
        {
          title: "Reorder anytime",
          body: "Repeat MCCB orders move faster — your spec history stays on file.",
        },
      ],
    },
    finalCta: {
      title: "Get an MCCB quote in hours",
      body: "Send the SKU or breaker spec — we'll come back with stock and a landed price.",
    },
  },

  "plc-modules-supplier": {
    slug: "plc-modules-supplier",
    breadcrumbLabel: "PLC Modules Supplier",
    eyebrow: "PLC sourcing",
    h1: "PLC Modules Supplier — Siemens, Allen-Bradley, Schneider",
    heroSub:
      "Specific PLC modules, I/O cards, communication modules, and HMIs sourced fast from verified U.S. suppliers. Single SKU replacement to multi-rack BOMs.",
    metaTitle: "PLC Modules Supplier | Siemens Allen-Bradley Schneider",
    metaDescription:
      "PLC modules supplier covering Siemens S7, Allen-Bradley ControlLogix, and Schneider Modicon. Verified U.S. stock and DDP delivery to LATAM in days.",
    pain: {
      title: "Why PLC sourcing is hard",
      items: [
        "Specific module SKUs are allocated globally and hard to find local",
        "Legacy PLC platforms (S7-300, SLC-500) need aftermarket sourcing",
        "Firmware revision matters — generic 'in stock' isn't enough",
        "Project commissioning blocked by one missing I/O card",
        "Local quote turnaround is too slow for a downtime event",
      ],
    },
    provide: {
      title: "What we source",
      items: [
        {
          title: "Siemens S7",
          body: "S7-1200, S7-1500, and aftermarket S7-300/400 modules.",
        },
        {
          title: "Allen-Bradley",
          body: "ControlLogix, CompactLogix, and legacy SLC-500 / PLC-5 modules.",
        },
        {
          title: "Schneider Modicon",
          body: "M340, M580, and Quantum platform modules and I/O cards.",
        },
        {
          title: "Comm & HMI",
          body: "Communication modules, HMIs, and panel PCs from major brands.",
        },
      ],
    },
    categories: {
      title: "Related pages",
      items: [
        { name: "PLC modules collection", href: "/fast-access-power-components/plc-modules" },
        { name: "Siemens S7 (Chile mining)", href: "/blog/siemens-s7-chile-mining-2026" },
        { name: "PLC suppliers (Peru mining)", href: "/blog/plc-suppliers-peru-mining-2026" },
        { name: "PLCs (Colombia oil & gas)", href: "/blog/plcs-colombia-oil-gas-2026" },
      ],
    },
    whyUs: {
      title: "Why source PLCs with us",
      items: [
        {
          title: "SKU-level accuracy",
          body: "We confirm exact part number and firmware before quoting.",
        },
        {
          title: "Aftermarket + new",
          body: "We can source legacy modules from verified aftermarket channels.",
        },
        {
          title: "Multi-brand RFQs",
          body: "Mix Siemens, AB, and Schneider on the same purchase order.",
        },
        {
          title: "DDP to LATAM",
          body: "Customs, duties, and final-mile delivery handled by us.",
        },
      ],
    },
    expectations: {
      title: "How to request a PLC module",
      items: [
        {
          title: "Send the part number",
          body: "Exact SKU + firmware revision (if relevant) gets the most accurate quote.",
        },
        {
          title: "Stock confirmed in hours",
          body: "We verify availability and lead time before pricing goes out.",
        },
        {
          title: "Firm landed price",
          body: "Product, freight, duties, and delivery — one number, no surprises.",
        },
        {
          title: "Delivery in days",
          body: "In-stock modules ship to major LATAM destinations in days.",
        },
      ],
    },
    finalCta: {
      title: "Send the PLC part number — quote in hours",
      body: "We source Siemens, Allen-Bradley, and Schneider modules from verified U.S. suppliers.",
    },
  },

  "ats-transfer-switch-supplier": {
    slug: "ats-transfer-switch-supplier",
    breadcrumbLabel: "ATS Transfer Switch Supplier",
    eyebrow: "ATS sourcing",
    h1: "ATS Transfer Switch Supplier — Critical Power Continuity",
    heroSub:
      "Automatic transfer switches and bypass-isolation equipment for data centers, hospitals, and industrial sites. Sourced fast and delivered DDP across LATAM.",
    metaTitle: "ATS Transfer Switch Supplier | Critical Power Continuity",
    metaDescription:
      "ATS transfer switch supplier for critical power applications. ASCO, Cummins, Eaton, and Generac equipment. Firm landed pricing and DDP delivery to LATAM.",
    pain: {
      title: "Why buyers source ATS through us",
      items: [
        "ATS replacement urgent after a critical power event",
        "New build needs ATS rated for specific kW and transition type",
        "Local supplier doesn't carry the brand specified by the engineer",
        "Bypass-isolation switches require coordination with switchgear",
        "Long OEM lead times don't fit project commissioning schedule",
      ],
    },
    provide: {
      title: "What we cover",
      items: [
        {
          title: "Major ATS brands",
          body: "ASCO, Cummins, Eaton, Generac, Kohler, and Russelectric.",
        },
        {
          title: "Open & closed transition",
          body: "Open transition (break-before-make) and closed transition ATS.",
        },
        {
          title: "Bypass-isolation",
          body: "Bypass-isolation ATS for service-without-shutdown applications.",
        },
        {
          title: "Service entrance rated",
          body: "SE-rated ATS for code-compliant service entrance applications.",
        },
      ],
    },
    whyUs: {
      title: "Why source ATS with us",
      items: [
        {
          title: "Critical-power focus",
          body: "We understand transition types, ratings, and code requirements.",
        },
        {
          title: "Brand flexibility",
          body: "We source the brand the engineer specified — not just what's local.",
        },
        {
          title: "Heavy-freight handling",
          body: "We coordinate freight on large ATS units to LATAM destinations.",
        },
        {
          title: "DDP delivery",
          body: "Customs, duties, and final-mile handled — one landed price.",
        },
      ],
    },
    expectations: {
      title: "How to request an ATS",
      items: [
        {
          title: "Send the spec or SKU",
          body: "Brand, amperage, voltage, transition type — or a model number.",
        },
        {
          title: "Quote in hours to days",
          body: "Standard models come back fast; engineered configs may run days.",
        },
        {
          title: "Approve and we ship",
          body: "We coordinate freight, customs, and delivery to your site.",
        },
        {
          title: "Delivered DDP",
          body: "ATS lands at the site with all duties and taxes settled.",
        },
      ],
    },
    finalCta: {
      title: "Source your ATS through one supplier",
      body: "Send the spec or SKU — we'll come back with availability and a firm landed price.",
    },
  },
  "electrical-components-supplier-latam-usa": {
    slug: "electrical-components-supplier-latam-usa",
    breadcrumbLabel: "Electrical Components Supplier LATAM",
    eyebrow: "Supplier · LATAM ↔ USA",
    h1: "Electrical Components Supplier for LATAM Buyers — Fast Sourcing of Breakers, Switchgear & Industrial Parts from the USA",
    heroSub:
      "Submit a part number, product request, or BOM. Get a fast quote with delivered pricing — freight, duties, taxes, and final-mile delivery across Latin America included.",
    metaTitle:
      "Electrical Components Supplier LATAM | Breakers, Switchgear & Parts from USA | Switzh",
    metaDescription:
      "Source breakers, switchgear, MCC buckets, relays and electrical components from the USA with fast RFQs and delivered pricing across LATAM. Request pricing now.",
    pain: {
      title: "If you're searching for a U.S. electrical components supplier",
      items: [
        "You need reliable supply when local distributors show no stock or 12+ week lead times",
        "You want direct access to U.S. inventory and manufacturer channels",
        "You need faster RFQ turnaround than traditional reps and brokers",
        "You're trying to reduce sourcing friction across cross-border procurement",
        "You're sourcing for contractors, EPC firms, utilities, data centers, mining, or industrial plants",
      ],
    },
    provide: {
      title: "What you can source",
      items: [
        {
          title: "Circuit breakers",
          body: "Eaton, Siemens, ABB, Schneider Electric, Square D — MCCBs, ICCBs, miniature and molded-case breakers across LV and MV.",
        },
        {
          title: "Switchgear & MCC components",
          body: "Switchgear parts, MCC buckets, motor control components, bus plugs, busway, and panelboard parts.",
        },
        {
          title: "Relays, contactors & controls",
          body: "Protective relays, control systems, contactors and control gear from Siemens, ABB, Schneider, Allen Bradley, GE Multilin.",
        },
        {
          title: "Distribution & retrofit",
          body: "Distribution panels, switchboards, retrofit kits, panel upgrades, and emergency replacement parts.",
        },
      ],
    },
    categories: {
      title: "High-intent part requests we quote daily",
      items: [
        { name: "Eaton FD3200 breaker — price & availability" },
        { name: "Siemens 3VA5110-6ED31-0AA0 sourcing" },
        { name: "ABB Tmax T5 250A breakers" },
        { name: "Schneider NSX250 breaker supply" },
        { name: "Square D QO panel breakers" },
        { name: "Siemens VL breaker pricing" },
        { name: "Siemens & Allen Bradley MCC buckets" },
        { name: "Eaton MCC bucket sourcing" },
        { name: "Siemens 3RT / Schneider LC1D / ABB AF contactors" },
        { name: "Siemens 7SJ & ABB protection relays" },
        { name: "Schneider Micom / GE Multilin relays" },
        { name: "Eaton & Square D panelboard components" },
        {
          name: "Urgent electrical components & replacements →",
          href: "/urgent-electrical-components",
        },
        {
          name: "How cross-border electrical sourcing works →",
          href: "/blog/fast-electrical-sourcing-latam-pricing-in-hours-2026",
        },
        {
          name: "Out-of-stock locally? Tap U.S. inventory →",
          href: "/out-of-stock-electrical-components-latam",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh",
      items: [
        {
          title: "Fast RFQ turnaround",
          body: "Most quotes come back the same day with availability, lead time, and a firm landed price.",
        },
        {
          title: "Access to U.S. supplier network",
          body: "Verified distributors and manufacturer channels — not a single inventory feed.",
        },
        {
          title: "Delivered pricing (DDP)",
          body: "Freight, duties, taxes, and final-mile delivery included upfront. No surprises at customs.",
        },
        {
          title: "Cross-border logistics handled",
          body: "Export, customs clearance, and final-mile delivery across Peru, Colombia, Mexico, Brazil, Chile and Central America.",
        },
        {
          title: "Hard-to-source components",
          body: "Allocated, discontinued, and obsolete parts located through the U.S. aftermarket and surplus network.",
        },
        {
          title: "Built for LATAM procurement",
          body: "A sourcing layer between U.S. supply and LATAM demand — designed for industrial buyers, contractors, and EPC firms.",
        },
      ],
    },
    expectations: {
      title: "How it works",
      items: [
        {
          title: "Submit your request",
          body: "Send a part number, product details, or full BOM. Photos and spec sheets also work.",
        },
        {
          title: "We source supply",
          body: "We query U.S. distributors and manufacturers to confirm real stock and lead time.",
        },
        {
          title: "Receive your quote",
          body: "Pricing, availability, and delivery timeline — landed at your site.",
        },
        {
          title: "Approve & fulfill",
          body: "We coordinate procurement, shipping, customs clearance, and final-mile delivery.",
        },
      ],
    },
    finalCta: {
      title: "Need pricing or availability?",
      body: "Submit your request and get a fast quote from U.S. suppliers — delivered pricing across LATAM included.",
    },
  },
  "urgent-electrical-components-replacement-latam": {
    slug: "urgent-electrical-components-replacement-latam",
    breadcrumbLabel: "Urgent Electrical Components Replacement",
    eyebrow: "Urgent · Replacement · LATAM",
    h1: "Urgent Electrical Components & Breaker Replacement for LATAM Buyers — Fast Sourcing of Switchgear, MCC Buckets & Industrial Parts from the USA",
    heroSub:
      "Submit a part number, replacement request, or BOM. Get a fast quote with delivered pricing — freight, duties, taxes, and final-mile delivery across Latin America included.",
    metaTitle:
      "Urgent Electrical Components LATAM | Breaker & Switchgear Replacement Fast | Switzh",
    metaDescription:
      "Need urgent breaker or switchgear replacement? Source electrical components fast from the USA with delivered pricing across LATAM. Request a quote now.",
    pain: {
      title: "Urgent replacement & downtime orders",
      items: [
        "Replacement breakers needed to bring a panel back online",
        "Switchgear components failed and the plant is down",
        "MCC buckets, contactors, or starters need immediate replacement",
        "Protective relays or control gear failed mid-operation",
        "Obsolete or discontinued parts blocking restart",
        "Local distributors quoting weeks when you need parts now",
      ],
    },
    provide: {
      title: "MCC & system failure replacements",
      items: [
        {
          title: "MCC bucket replacements",
          body: "Siemens, Allen Bradley, and Eaton MCC buckets sourced for urgent motor control failures.",
        },
        {
          title: "Motor starters & contactors",
          body: "Replacement starters, contactors, and control gear when a motor control panel goes down.",
        },
        {
          title: "Breaker & switchgear replacements",
          body: "Eaton, Siemens, ABB, Schneider, and Square D breakers plus switchgear components for emergency restoration.",
        },
        {
          title: "Relay & control replacements",
          body: "Protective relays and control components sourced fast for industrial system failures.",
        },
      ],
    },
    categories: {
      title: "What you can source fast",
      items: [
        { name: "Circuit breakers — Eaton, Siemens, ABB, Schneider Electric, Square D" },
        { name: "Switchgear components & retrofit kits" },
        { name: "MCC buckets and motor control systems" },
        { name: "Protective relays" },
        { name: "Contactors and starters" },
        { name: "Distribution panels and switchboards" },
        { name: "Bus plugs and busway systems" },
        { name: "Emergency replacement parts" },
        { name: "Low and medium voltage components" },
        { name: "Obsolete & discontinued breaker alternatives" },
        {
          name: "U.S. supplier sourcing for LATAM →",
          href: "/electrical-components-supplier-latam-usa",
        },
        {
          name: "How fast electrical sourcing works →",
          href: "/blog/how-to-source-electrical-components-latam-fast",
        },
        {
          name: "Fast access power components →",
          href: "/fast-access-power-components",
        },
        {
          name: "Out-of-stock locally? Tap U.S. inventory →",
          href: "/out-of-stock-electrical-components-latam",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh for urgent orders",
      items: [
        {
          title: "Fast RFQ turnaround",
          body: "Quotes typically returned the same day with availability, lead time, and a firm landed price.",
        },
        {
          title: "Access to U.S. supplier network",
          body: "Verified distributors, manufacturer channels, and aftermarket sources — not a single inventory feed.",
        },
        {
          title: "Delivered pricing — no surprises",
          body: "Product cost, freight, duties, taxes, and final-mile delivery included upfront (DDP).",
        },
        {
          title: "Cross-border logistics handled",
          body: "Export, customs clearance, and final-mile delivery across Peru, Colombia, Mexico, Brazil, Chile and Central America.",
        },
        {
          title: "Hard-to-find component support",
          body: "Obsolete breakers, discontinued Siemens components, and ABB alternatives located through U.S. surplus and aftermarket networks.",
        },
        {
          title: "Built for urgent industrial procurement",
          body: "A sourcing layer designed for downtime, replacement, and emergency RFQs — not catalog browsing.",
        },
      ],
    },
    expectations: {
      title: "How fast sourcing works",
      items: [
        {
          title: "Submit your request",
          body: "Send a part number, product description, BOM, and urgency level. Photos and spec sheets also work.",
        },
        {
          title: "We source supply",
          body: "We query U.S. distributors, manufacturer networks, and alternative sourcing channels to confirm real stock.",
        },
        {
          title: "Receive your quote",
          body: "Product cost, freight, duties, taxes, and delivery timeline — landed at your site.",
        },
        {
          title: "Approve & fulfill",
          body: "We coordinate procurement, shipping, customs clearance, and final-mile delivery.",
        },
      ],
    },
    finalCta: {
      title: "Need urgent pricing or availability?",
      body: "Submit your replacement request or BOM and get a fast quote from U.S. suppliers — delivered pricing across LATAM included.",
    },
  },
  "electrical-components-specs-guides-latam": {
    slug: "electrical-components-specs-guides-latam",
    breadcrumbLabel: "Electrical Components Specs & Guides",
    eyebrow: "Specs · Guides · Research",
    h1: "Electrical Components Specifications, Guides & Sourcing for LATAM Buyers — Breakers, Switchgear, MCC Buckets & Industrial Electrical Systems",
    heroSub:
      "Access technical guides, compare components, and source breakers, switchgear, MCC buckets and relays from the USA with fast quotes and delivered pricing across LATAM.",
    metaTitle:
      "Electrical Components Specs & Guides LATAM | Breakers, Switchgear, MCC | Switzh",
    metaDescription:
      "Find breaker sizing guides, switchgear specs, MCC bucket details and electrical manuals. Source components fast from the USA with RFQs and delivered pricing.",
    pain: {
      title: "Common technical research searches before procurement",
      items: [
        "MCC bucket specifications and sizing",
        "Breaker sizing guide for industrial panels",
        "Switchgear specifications (LV vs MV) and PDF references",
        "Relay settings and protection coordination guides",
        "Transformer selection and load-matching",
        "OEM manuals: Siemens, ABB, Schneider, Eaton, Square D",
      ],
    },
    provide: {
      title: "Breaker, switchgear & MCC comparisons",
      items: [
        {
          title: "LV vs MV switchgear",
          body: "Voltage class, application envelope, fault-current ratings, and when to spec each — for industrial, infrastructure, and data center loads.",
        },
        {
          title: "Breaker types explained",
          body: "MCB vs MCCB vs ICCB vs ACB. Trip units, frame sizes, interrupting ratings, and selectivity considerations across Eaton, Siemens, ABB, Schneider, and Square D.",
        },
        {
          title: "MCC vs panelboard",
          body: "When motor control centers are required versus a panelboard with starters — bucket types, withdrawable units, and motor starter sizing.",
        },
        {
          title: "Relays & contactors",
          body: "Protective relay families (Siemens 7SJ, ABB REF/REM, Schneider Micom, GE Multilin) and contactor vs breaker selection for control circuits.",
        },
      ],
    },
    categories: {
      title: "Spec & manual searches we help bridge to sourcing",
      items: [
        { name: "Siemens breaker manual PDF" },
        { name: "ABB relay manual" },
        { name: "Schneider panel installation guide" },
        { name: "Eaton switchgear manual" },
        { name: "Square D QO panelboard reference" },
        { name: "Breaker sizing guide industrial" },
        { name: "Switchgear specifications PDF (LV / MV)" },
        { name: "MCC bucket specifications & motor starter sizing" },
        { name: "Relay settings & protection coordination" },
        { name: "Transformer selection guide" },
        {
          name: "Have specs ready? Talk to a supplier →",
          href: "/electrical-components-supplier-latam-usa",
        },
        {
          name: "Need it now? Urgent replacement sourcing →",
          href: "/urgent-electrical-components-replacement-latam",
        },
        {
          name: "How to source electrical components LATAM (blog) →",
          href: "/blog/how-to-source-electrical-components-latam-fast",
        },
        {
          name: "Electrical components supplier LATAM (blog) →",
          href: "/blog/electrical-components-supplier-latam",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh",
      items: [
        {
          title: "Research → RFQ in one workflow",
          body: "Once specs are confirmed, the same team handles the RFQ, sourcing, and delivered pricing — no handoff to a separate distributor.",
        },
        {
          title: "OEM-aligned coverage",
          body: "Eaton, Siemens, ABB, Schneider Electric, and Square D — sourced through verified U.S. distribution and manufacturer channels.",
        },
        {
          title: "Delivered pricing (DDP)",
          body: "Product, freight, duties, taxes, and final-mile delivery included upfront. The price you approve is the price you pay.",
        },
        {
          title: "Cross-border logistics handled",
          body: "Export, customs clearance, and final-mile delivery across Peru, Colombia, Mexico, Brazil, Chile and Central America.",
        },
        {
          title: "Technical sourcing support",
          body: "Help interpreting nameplates, identifying compatible alternatives, and validating spec matches before quoting.",
        },
        {
          title: "Standards-aware",
          body: "U.S.-sourced industrial components aligned with IEC/IEEE references commonly accepted across LATAM industrial projects.",
        },
      ],
    },
    expectations: {
      title: "From research to sourcing",
      items: [
        {
          title: "Review specifications",
          body: "Confirm voltage, load, interrupting rating, environment, and protection requirements.",
        },
        {
          title: "Compare components",
          body: "MCB vs MCCB, MCC vs panelboard, LV vs MV switchgear, contactor vs breaker — narrow to the right SKU family.",
        },
        {
          title: "Identify part or system",
          body: "Lock in OEM part numbers, frame sizes, trip units, and accessories — or share a BOM.",
        },
        {
          title: "Submit RFQ",
          body: "Send the part number, BOM, or spec sheet. Receive availability, lead time, and a firm landed price across LATAM.",
        },
      ],
    },
    finalCta: {
      title: "Need pricing or availability after your research?",
      body: "Submit your spec, part number, or BOM and get a fast quote from U.S. suppliers — delivered pricing across LATAM included.",
    },
  },
  "electrical-components-sourcing-platform-latam": {
    slug: "electrical-components-sourcing-platform-latam",
    breadcrumbLabel: "Electrical Sourcing Platform · LATAM",
    eyebrow: "Platform · Cross-Border · LATAM",
    h1: "Electrical Components Sourcing Platform for LATAM — How Cross-Border Procurement of U.S. Switchgear & Industrial Electrical Equipment Actually Works",
    heroSub:
      "Understand how cross-border electrical sourcing actually works in 2026 — and why platform-based procurement is replacing traditional distributors and reps for industrial buyers across Latin America.",
    metaTitle:
      "Electrical Components LATAM | Switchgear & Industrial Supply Platform | Switzh",
    metaDescription:
      "How cross-border electrical sourcing platforms work for LATAM — and why platform-based procurement is replacing traditional distributors and reps for switchgear, breakers and industrial equipment from the USA.",
    pain: {
      title: "If you're searching for electrical components or switchgear in LATAM",
      items: [
        "Switchgear LATAM, switchgear Latin America supplier",
        "Electrical components LATAM and power distribution components",
        "Industrial electrical supply LATAM and electrical equipment supplier Latin America",
        "Cross-border electrical supply and import electrical equipment into LATAM",
        "U.S. electrical parts with international shipping into LATAM",
        "Electrical sourcing platform / industrial sourcing platform for procurement teams",
      ],
    },
    provide: {
      title: "What you can source through the platform",
      items: [
        {
          title: "Circuit breakers & switchgear",
          body: "Eaton, Siemens, ABB, Schneider Electric, and Square D — MCCBs, ICCBs, miniature and molded-case breakers, switchgear components, and retrofit kits across LV and MV.",
        },
        {
          title: "MCC, motor control & relays",
          body: "MCC buckets, motor control systems, contactors, starters, and protective relays from Siemens, Allen Bradley, ABB, Schneider, and GE Multilin.",
        },
        {
          title: "Distribution & busway",
          body: "Panelboards, switchboards, busway systems, bus plugs, and power distribution components for industrial and infrastructure projects.",
        },
        {
          title: "Cross-border logistics",
          body: "Single workflow for sourcing, freight, customs clearance, and final-mile delivery across Peru, Colombia, Mexico, Brazil, Chile, and Central America.",
        },
      ],
    },
    categories: {
      title: "Industrial procurement intent we serve in LATAM",
      items: [
        { name: "Industrial procurement LATAM" },
        { name: "Electrical infrastructure LATAM" },
        { name: "Industrial electrical supplier" },
        { name: "Electrical parts distributor" },
        { name: "Switchgear supplier — Peru" },
        { name: "Electrical components — Colombia" },
        { name: "Breaker supplier — Mexico" },
        { name: "Industrial supply — Brazil" },
        { name: "Siemens vs ABB switchgear" },
        { name: "Schneider vs Eaton breakers" },
        { name: "Best switchgear brands & component comparison" },
        {
          name: "Need a supplier? Electrical components supplier (USA → LATAM) →",
          href: "/electrical-components-supplier-latam-usa",
        },
        {
          name: "Need it now? Urgent electrical components & replacement →",
          href: "/urgent-electrical-components-replacement-latam",
        },
        {
          name: "Researching specs? Specs & guides hub →",
          href: "/electrical-components-specs-guides-latam",
        },
        {
          name: "How to source electrical components LATAM (blog) →",
          href: "/blog/how-to-source-electrical-components-latam-fast",
        },
        {
          name: "Urgent electrical components in LATAM (blog) →",
          href: "/blog/urgent-electrical-components-latam",
        },
        {
          name: "Electrical components specs & guides (blog) →",
          href: "/blog/electrical-components-specs-guides-latam",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh",
      items: [
        {
          title: "Platform-based sourcing model",
          body: "A single workflow for RFQ, sourcing, pricing, and logistics — replacing fragmented distributor and freight-forwarder coordination.",
        },
        {
          title: "Fast RFQ turnaround",
          body: "Most quotes come back the same day with availability, lead time, and a firm landed price.",
        },
        {
          title: "Access to U.S. supplier network",
          body: "Verified distributors and manufacturer channels — Eaton, Siemens, ABB, Schneider, Square D — not a single inventory feed.",
        },
        {
          title: "Delivered pricing (DDP)",
          body: "Product, freight, duties, taxes, and final-mile delivery included upfront. No surprises at customs.",
        },
        {
          title: "Cross-border logistics handled",
          body: "Export, customs clearance, and final-mile delivery across Peru, Colombia, Mexico, Brazil, Chile and Central America.",
        },
        {
          title: "Built for LATAM procurement",
          body: "Designed for industrial buyers, contractors, EPC firms, utilities, data centers, mining, and infrastructure projects.",
        },
      ],
    },
    expectations: {
      title: "From generic search to procurement",
      items: [
        {
          title: "Search & explore",
          body: "Buyers start with broad queries — switchgear LATAM, electrical components LATAM, industrial electrical supply.",
        },
        {
          title: "Compare suppliers & brands",
          body: "Narrow to the right OEM family across Eaton, Siemens, ABB, Schneider, and Square D.",
        },
        {
          title: "Submit RFQ",
          body: "Send a part number, BOM, or sourcing requirement. The platform queries U.S. supply in parallel.",
        },
        {
          title: "Approve & fulfill",
          body: "Procurement, shipping, customs clearance, and final-mile delivery — coordinated end-to-end.",
        },
      ],
    },
    finalCta: {
      title: "Ready to source electrical components in LATAM?",
      body: "Submit your request and get fast RFQs with delivered pricing from U.S. suppliers — across Peru, Colombia, Mexico, Brazil, Chile, and Central America.",
    },
  },
};

/** Stable slug list for routing + sitemap generation. */
export const SEO_LANDING_SLUGS = Object.keys(SEO_LANDINGS);